import React, { useState } from 'react';
import { Task, TaskStage } from '../../../../types/Task';
import { taskService } from '../../../../services/quantifiedTaskService';
import { useAuth } from '../../../../contexts/AuthContext';
import { Button, Alert, Form, Modal } from '@/components/ui';

interface ReopenTaskModalProps {
  visible: boolean;
  onClose: () => void;
  task: Task;
  onSubmit: (comments: string) => void;
}

const ReopenTaskModal: React.FC<ReopenTaskModalProps> = ({
  visible,
  onClose,
  task,
  onSubmit,
}) => {
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comments.trim() || !currentUser?.id) return;

    try {
      setLoading(true);
      
      // Allow reopening of closed, cancelled, or finalized tasks
      if (task.status !== TaskStage.CLOSED && task.status !== TaskStage.CANCELLED && task.status !== TaskStage.FINALIZED) {
        throw new Error('Only closed, cancelled, or finalized tasks can be reopened');
      }

      await onSubmit(comments);
      setComments('');
    } catch (error) {
      console.error('Error reopening task:', error);
      alert(error instanceof Error ? error.message : 'Failed to reopen task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={visible} onHide={onClose} backdrop={loading ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>Reopen Task</Modal.Title>
      </Modal.Header>
      {/* The form wraps Body+Footer so the footer's submit button still submits it; it must
          be the flex column itself, otherwise Body can't flex and the footer stops pinning. */}
      <form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body>
          <Alert variant="info">
            <i className="bi bi-info-circle tw-mr-2"></i>
            Please provide a reason for reopening this task. This will change the task status from {task.status === TaskStage.FINALIZED ? 'Finalized' : task.status === TaskStage.CLOSED ? 'Closed' : 'Cancelled'} to Active.
          </Alert>
          <Form.Group className="tw-mb-3">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              required
              placeholder="Please provide your comments..."
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || !comments.trim()}
          >
            {loading ? 'Reopening...' : 'Reopen Task'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ReopenTaskModal; 